import { useEffect, useState } from 'react';
import { Button, Card, Statistic, Row, Col, notification } from 'antd';
import { useLocalProducts } from '~/hooks';
import indexedDBService from '~/services/indexedDBService';

/**
 * Component để test và debug IndexedDB
 * Thêm vào trang để kiểm tra sync
 */
function IndexedDBDebug() {
    const {
        isReady,
        isSyncing,
        syncStatus,
        syncProducts,
        forceFullSync,
    } = useLocalProducts();

    const [dbInfo, setDbInfo] = useState({
        exists: false,
        stores: [],
        productCount: 0,
        meta: {}
    });

    // Check IndexedDB status
    useEffect(() => {
        const checkDB = async () => {
            try {
                // Check if database exists
                const databases = await indexedDB.databases();
                const gctPosDb = databases.find(db => db.name === 'gct_pos_db');

                if (gctPosDb) {
                    console.log('✅ Database exists:', gctPosDb);

                    // Get more info
                    await indexedDBService.init();
                    const productCount = await indexedDBService.getProductCount();
                    const syncVersion = await indexedDBService.getMeta('sync_product_version');
                    const syncTime = await indexedDBService.getMeta('sync_product_time');

                    setDbInfo({
                        exists: true,
                        stores: ['products', 'meta'],
                        productCount,
                        meta: {
                            sync_product_version: syncVersion,
                            sync_product_time: syncTime
                        }
                    });
                } else {
                    console.log('❌ Database does not exist');
                    setDbInfo({
                        exists: false,
                        stores: [],
                        productCount: 0,
                        meta: {}
                    });
                }
            } catch (error) {
                console.error('Error checking database:', error);
            }
        };

        if (isReady) {
            checkDB();
        }
    }, [isReady, syncStatus]);

    const handleSync = async () => {
        const result = await syncProducts();

        if (result.success) {
            notification.success({
                message: 'Đồng bộ thành công',
                description: `Đã thêm ${result.added}, cập nhật ${result.updated}, xóa ${result.deleted} sản phẩm. Tổng: ${result.total}`
            });
        } else {
            notification.error({
                message: 'Đồng bộ thất bại',
                description: result.message
            });
        }
    };

    const handleForceFullSync = async () => {
        const result = await forceFullSync();

        if (result.success) {
            notification.success({
                message: 'Đồng bộ toàn bộ thành công',
                description: `Tổng: ${result.total} sản phẩm`
            });
        } else {
            notification.error({
                message: 'Đồng bộ thất bại',
                description: result.message
            });
        }
    };

    const handleClearDB = async () => {
        try {
            await indexedDBService.init();
            await indexedDBService.clearProducts();
            await indexedDBService.setMeta('sync_product_version', null);
            await indexedDBService.setMeta('sync_product_time', null);

            notification.success({
                message: 'Đã xóa dữ liệu',
                description: 'IndexedDB đã được xóa sạch'
            });

            // Reload to refresh
            setTimeout(() => window.location.reload(), 1000);
        } catch (error) {
            notification.error({
                message: 'Lỗi',
                description: error.message
            });
        }
    };

    const handleDeleteDB = async () => {
        try {
            await indexedDB.deleteDatabase('gct_pos_db');
            notification.success({
                message: 'Đã xóa database',
                description: 'IndexedDB đã được xóa hoàn toàn'
            });

            setTimeout(() => window.location.reload(), 1000);
        } catch (error) {
            notification.error({
                message: 'Lỗi',
                description: error.message
            });
        }
    };

    return (
        <Card title="🔍 IndexedDB Debug Panel" style={{ margin: 20 }}>
            <Row gutter={16} style={{ marginBottom: 20 }}>
                <Col span={6}>
                    <Statistic
                        title="Database Status"
                        value={dbInfo.exists ? 'Exists' : 'Not Found'}
                        valueStyle={{ color: dbInfo.exists ? '#3f8600' : '#cf1322' }}
                    />
                </Col>
                <Col span={6}>
                    <Statistic
                        title="Product Count"
                        value={dbInfo.productCount}
                    />
                </Col>
                <Col span={6}>
                    <Statistic
                        title="Sync Version"
                        value={dbInfo.meta.sync_product_version || 'N/A'}
                    />
                </Col>
                <Col span={6}>
                    <Statistic
                        title="Status"
                        value={isSyncing ? 'Syncing...' : (isReady ? 'Ready' : 'Loading')}
                        valueStyle={{ color: isReady ? '#3f8600' : '#1890ff' }}
                    />
                </Col>
            </Row>

            <Row gutter={16} style={{ marginBottom: 20 }}>
                <Col span={24}>
                    <p><strong>Sync Time:</strong> {
                        dbInfo.meta.sync_product_time
                            ? new Date(dbInfo.meta.sync_product_time * 1000).toLocaleString()
                            : 'Never'
                    }</p>
                    <p><strong>Stores:</strong> {dbInfo.stores.join(', ') || 'None'}</p>
                </Col>
            </Row>

            <Row gutter={16}>
                <Col>
                    <Button
                        type="primary"
                        onClick={handleSync}
                        loading={isSyncing}
                        disabled={!isReady}
                    >
                        🔄 Đồng bộ Delta
                    </Button>
                </Col>
                <Col>
                    <Button
                        onClick={handleForceFullSync}
                        loading={isSyncing}
                        disabled={!isReady}
                    >
                        🔄 Đồng bộ toàn bộ
                    </Button>
                </Col>
                <Col>
                    <Button
                        onClick={handleClearDB}
                        disabled={!isReady || !dbInfo.exists}
                    >
                        🗑️ Xóa dữ liệu
                    </Button>
                </Col>
                <Col>
                    <Button
                        danger
                        onClick={handleDeleteDB}
                        disabled={!dbInfo.exists}
                    >
                        ❌ Xóa Database
                    </Button>
                </Col>
                <Col>
                    <Button onClick={() => window.location.reload()}>
                        🔃 Reload Page
                    </Button>
                </Col>
            </Row>

            <div style={{ marginTop: 20, padding: 10, background: '#f0f0f0', borderRadius: 4 }}>
                <h4>📋 Hướng dẫn:</h4>
                <ol>
                    <li>Mở Developer Tools (F12)</li>
                    <li>Vào tab Console để xem logs</li>
                    <li>Vào tab Application → Storage → IndexedDB để xem database</li>
                    <li>Bấm "Đồng bộ Delta" để sync lần đầu</li>
                    <li>Kiểm tra database đã có dữ liệu chưa</li>
                </ol>
            </div>
        </Card>
    );
}

export default IndexedDBDebug;
